export type HelpdeskSupportType =
    | 'ProductTechnicalSupport'
    | 'PricingOrderSupport'
    | 'CommercialSupport'
    | 'SystemTechnicalSupport';

export type HelpdeskProduct = string;

export interface CreateHelpdeskCaseRequest {
    subject: string;
    description: string;
    product: HelpdeskProduct;
    supportType?: HelpdeskSupportType;
    includeChatResponse?: boolean;
    latestUserRequest?: string;
    latestChatResponse?: string;
    conversationTurns?: {
        userRequest?: string;
        assistantResponse?: string;
    }[];
    userToken?: string;
}

export interface CreateHelpdeskCaseResponse {
    id: string;
    salesforceId: string;
    caseNumber?: string | null;
    attachments?: unknown[];
    hasFailures?: boolean;
}

const getPowerSource = async (getSalesforceTokenFn?: () => Promise<string | undefined>) => {
    if (getSalesforceTokenFn) {
        try {
            const token = await getSalesforceTokenFn();
            console.debug('Retrieved Salesforce token:', token);
             console.log('Retrieved Salesforce token:', token);
            if (token) {
                return token;
            }
        } catch (error) {
            console.error('Error retrieving Salesforce token:', error);
        }
    }
    // Fallback if token retrieval fails
    return undefined;
};

const sanitizeDescription = (value?: string) => {
    if (!value) {
        return '';
    }

    return Array.from(value).filter((char) => {
        const codePoint = char.charCodeAt(0);
        return codePoint > 0x1f && codePoint !== 0x7f;
    }).join('').trim();
};

// Converts markdown syntax to plain readable text for ticket systems that don't render markdown
const stripMarkdown = (text: string): string => {
    return text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')   // [text](url) → text
        .replace(/\*\*(.+?)\*\*/g, '$1')             // **bold** → bold
        .replace(/\*(.+?)\*/g, '$1')                 // *italic* → italic
        .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')      // `code` / ```code``` → code
        .replace(/^#{1,6}\s+/gm, '')                 // ## Heading → Heading
        .replace(/^---+$/gm, '─'.repeat(40))         // --- → divider line
        .replace(/^[-*]\s+/gm, '• ')                 // - item → • item
        .replace(/\n{3,}/g, '\n\n')                  // collapse excess blank lines
        .trim();
};

// Like sanitizeDescription but preserves newlines so stripMarkdown line anchors work
const sanitizeChatText = (value: string): string =>
    Array.from(value).filter((char) => {
        const cp = char.charCodeAt(0);
        return cp === 0x09 || cp === 0x0A || cp === 0x0D || (cp > 0x1F && cp !== 0x7F);
    }).join('').trim();

const INPUT_DESCRIPTION_DIVIDER = '------------------------------------------------------------------------------------';
const CONVERSATION_DIVIDER = '=================================================';

const formatConversationTurn = (turn: { userRequest?: string; assistantResponse?: string; }) => {
    const trimmedUserRequest = sanitizeDescription(turn.userRequest);
    const trimmedAssistantResponse = turn.assistantResponse ? stripMarkdown(sanitizeChatText(turn.assistantResponse)) : '';

    if (!trimmedUserRequest && !trimmedAssistantResponse) {
        return '';
    }

    return [
        'User Request:',
        trimmedUserRequest,
        '',
        'Assistant Response:',
        trimmedAssistantResponse,
    ].join('\n');
};

const formatConversationSection = (conversationTurns: NonNullable<CreateHelpdeskCaseRequest['conversationTurns']>) => {
    const formattedTurns = conversationTurns
        .map(formatConversationTurn)
        .filter((turn) => turn.length > 0);

    return formattedTurns.join(`\n${CONVERSATION_DIVIDER}\n`);
};

export const buildDescriptionForPayload = ({ description, includeChatResponse, latestUserRequest, latestChatResponse, conversationTurns }: Pick<CreateHelpdeskCaseRequest, 'description' | 'includeChatResponse' | 'latestUserRequest' | 'latestChatResponse' | 'conversationTurns'>) => {
    const trimmedUserDescription = sanitizeDescription(description);

    if (!includeChatResponse) {
        return trimmedUserDescription;
    }

    const normalizedTurns = conversationTurns && conversationTurns.length > 0
        ? conversationTurns
        : [{ userRequest: latestUserRequest, assistantResponse: latestChatResponse }];

    const chatSection = formatConversationSection(normalizedTurns);
    if (!chatSection) {
        return trimmedUserDescription;
    }

    return `${trimmedUserDescription}\n${INPUT_DESCRIPTION_DIVIDER}\n${chatSection}`;
};

export const createHelpdeskCase = async (payload: CreateHelpdeskCaseRequest, getSalesforceTokenFn?: () => Promise<string | undefined>): Promise<CreateHelpdeskCaseResponse | string> => {
    const formData = new FormData();
    const description = buildDescriptionForPayload(payload);
    const userToken = payload.userToken?.trim();

    formData.append('subject', payload.subject);
    formData.append('description', description);
    formData.append('product', payload.product);

    if (payload.supportType) {
        formData.append('supportType', payload.supportType);
    }
    // Debug: log form values to console to verify payload matches Swagger
    try {
        for (const [k, v] of Array.from(formData.entries())) {
            try { console.debug('[helpdesk] form:', k, v); } catch (e) { /* ignore */ }
        }

        const apiUrl = 'https://danfoss-sourceuserworkspace-e2e-neu-api.azurewebsites.net/plus1/cases';
        const authToken = await getPowerSource(getSalesforceTokenFn);

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                ...(authToken && { Authorization: `Bearer ${authToken}` }),
                'x-user-token': userToken|| authToken || '',
                Accept: 'text/plain',
                'Access-Control-Allow-Origin': '*'
            },
            body: formData,
        });

        if (!response.ok) {
            let bodyText: string;
            try {
                bodyText = await response.text();
            } catch (e) {
                bodyText = '<unable to read response body>';
            }
            throw new Error(`Helpdesk API error ${response.status}: ${bodyText}`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            return await response.json();
        }

        // fallback to plain text when API returns text
        return await response.text();
    } catch (err) {
        // Surface network / CORS / other fetch errors with a helpful message
        const message = err instanceof Error ? err.message : String(err);
        throw new Error(`Network error when creating helpdesk case: ${message}`);
    }
};

export default createHelpdeskCase;
